import React from "react";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { FaSpinner } from "react-icons/fa";
import SVGIcon from "../../components/DynamicIcon";
import { BTN_SX } from "./formSubmissionStyles";

// Renders the full action button bar shown in the form submission header.
// Accepts pre-built onClick handlers from SubmissionDynamicform so this
// component stays purely presentational — no form state or RHF references.
const ActionButtons = ({
  // Visibility / mode flags
  showDeleteButton,
  showNext,
  showButtonShow,
  viewOnly,
  ModalIsOpen,
  // Approval state
  isApproved,
  isSecondApproved,
  showApprovePermission,
  showSecondApprovePermission,
  // Loading state
  isLoadingBtn,
  isSaveAndNew,
  isPrintLoading,
  // Print
  isPrint,
  templatePdfId,
  // Button labels (dynamic — set per subform)
  submitButtonLabel,
  nextButtonLabel,
  // Handlers — all pre-bound by the parent
  onDelete,
  onRejectConfirm,
  onApprovalConfirm,
  onSaveClose,
  onSubmit,
  onSaveAndNew,
  onNext,
  onSecondRejectConfirm,
  onSecondApprovalConfirm,
  onPrint,
}) => {
  const showFirstApproval =
    isApproved !== undefined &&
    isApproved !== null &&
    Number(isApproved) === -1 &&
    !showNext &&
    showApprovePermission;

  const showSecondApproval =
    isSecondApproved !== undefined &&
    isSecondApproved !== null &&
    Number(isSecondApproved) === -1 &&
    Number(isApproved) === 1 &&
    !showNext &&
    showSecondApprovePermission;

  return (
    <div className={!ModalIsOpen ? "form_button_new_row fixed-form_button_new_row1" : "form_button_new_row"}>

      {/* ── Delete ── */}
      {showDeleteButton && (
        <IconButton
          sx={BTN_SX.deleteIcon}
          onClick={onDelete}
          type="button"
          title="Delete"
          aria-label="Delete submission"
        >
          <SVGIcon name="deleteBag" width={25} height={25} />
        </IconButton>
      )}

      {/* ── First-level approval: Reject / Approve ── */}
      {showFirstApproval && (
        <>
          <Button
            sx={BTN_SX.rejectOutlined}
            variant="text"
            startIcon={<SVGIcon name="rejectIcon" />}
            onClick={onRejectConfirm}
          >
            Reject
          </Button>
          <Button
            sx={BTN_SX.approveOutlined}
            variant="text"
            startIcon={<SVGIcon name="approveIcon" stroke="#26AC5C" />}
            onClick={onApprovalConfirm}
          >
            Approve
          </Button>
        </>
      )}

      {/* ── Save & Close (multi-subform flow only) ── */}
      {showNext && !viewOnly && (
        <Button
          sx={{ ...BTN_SX.saveClose, ml: '5px' }}
          className="form_btn_save_margin"
          type="button"
          variant="text"
          disabled={isLoadingBtn || viewOnly}
          onClick={onSaveClose}
          title="Save & Close"
        >
          {isLoadingBtn && !isSaveAndNew ? "Saving..." : "Save & Close"}
        </Button>
      )}

      {/* ── Submit / Save ── */}
      {!showNext && !showButtonShow && !viewOnly && (
        <Button
          sx={BTN_SX.saveClose}
          className="form_btn_save_margin"
          type="submit"
          variant="text"
          title="Submit (Ctrl+Enter)"
          disabled={isLoadingBtn || viewOnly}
          onClick={onSubmit}
        >
          {isLoadingBtn && !isSaveAndNew && <FaSpinner className="btn-spinner" />}
          {isLoadingBtn && !isSaveAndNew ? "Saving..." : submitButtonLabel}
        </Button>
      )}

      {/* ── Save & New ── */}
      {!showNext && !showButtonShow && !ModalIsOpen && !viewOnly && (
        <Button
          sx={BTN_SX.saveNew}
          className="form_btn_saveAndNew_margin"
          variant="text"
          disabled={isSaveAndNew || viewOnly}
          onClick={onSaveAndNew}
        >
          {isSaveAndNew && <FaSpinner className="btn-spinner" />}
          {isSaveAndNew ? "Saving..." : "Save & New"}
        </Button>
      )}

      {/* ── Next (multi-subform flow) ── */}
      {showNext && !viewOnly && (
        <Button
          sx={BTN_SX.saveNew}
          className="form_btn_saveAndNew_margin"
          type="submit"
          variant="text"
          title="Next (Ctrl+Enter)"
          disabled={isLoadingBtn || viewOnly}
          onClick={onNext}
          endIcon={<NavigateNextIcon />}
        >
          {nextButtonLabel}
        </Button>
      )}

      {/* ── Second-level approval: Reject / Approve ── */}
      {showSecondApproval && (
        <>
          <Button
            sx={BTN_SX.rejectOutlined}
            variant="text"
            startIcon={<SVGIcon name="rejectIcon" />}
            onClick={onSecondRejectConfirm}
          >
            Reject
          </Button>
          <Button
            sx={BTN_SX.approveOutlined}
            variant="text"
            startIcon={<SVGIcon name="approveIcon" stroke="#26AC5C" />}
            onClick={onSecondApprovalConfirm}
          >
            Approve
          </Button>
        </>
      )}

      {/* ── Print ── */}
      {(isPrint || templatePdfId) && (
        <IconButton
          aria-label="Print as PDF"
          title="Print"
          className="printButton"
          disabled={isPrintLoading}
          onClick={onPrint}
        >
          <SVGIcon name="PDFprintIcon" width={45} height={32} />
        </IconButton>
      )}
    </div>
  );
};

export default ActionButtons;
